<script setup lang="ts">
    import { computed } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import type { ExpressionKey } from '@/js/consts/language/en';
    import { dateToString } from '@/js/utils/dateUtils';
    import EchoLabel from '@/js/components/EchoLabel.vue';
    import {
        getKeyIndicateSyncRunStatus,
        type KeyIndicateSyncRunStatus,
        type KeyIndicateSyncSystem,
    } from '@/js/services/api/backOffice/keyIndicateSyncSettingsApi';

    const props = defineProps<{
        system: KeyIndicateSyncSystem;
    }>();

    const statusDisplay: Record<
        KeyIndicateSyncRunStatus,
        { label: ExpressionKey; variant: 'light' | 'success' | 'error' }
    > = {
        running: {
            label: 'back-office-key-indicate-sync-settings-status-running',
            variant: 'light',
        },
        succeeded: {
            label: 'back-office-key-indicate-sync-settings-status-succeeded',
            variant: 'success',
        },
        failed: {
            label: 'back-office-key-indicate-sync-settings-status-failed',
            variant: 'error',
        },
    };

    const status = computed(() => {
        const runStatus = getKeyIndicateSyncRunStatus(props.system);
        return runStatus ? statusDisplay[runStatus] : null;
    });
</script>

<template>
    <div
        v-if="system.lastRunStartedAt && status"
        class="flex flex-wrap gap-x-10 gap-y-3 rounded bg-gray-extralight px-4 py-3"
    >
        <div class="flex flex-col gap-1">
            <span class="text-overline">
                {{ __('back-office-key-indicate-sync-settings-last-run') }}
            </span>
            <span class="text-body-bold">{{ dateToString(system.lastRunStartedAt) }}</span>
        </div>
        <div class="flex flex-col gap-1">
            <span class="text-overline">
                {{ __('back-office-key-indicate-sync-settings-last-success') }}
            </span>
            <span class="text-body-bold">
                {{ system.lastSuccessAt ? dateToString(system.lastSuccessAt) : '-' }}
            </span>
        </div>
        <div class="flex flex-col items-start gap-1">
            <span class="text-overline">
                {{ __('back-office-key-indicate-sync-settings-status') }}
            </span>
            <EchoLabel :variant="status.variant">{{ __(status.label) }}</EchoLabel>
        </div>
    </div>
</template>
